import React, { useEffect, useState } from "react";
import { Card, Tabs, Select, Space, Tag, message, Statistic, Row, Col, Input } from "antd";
import {
  scReportPartyBalance, scReportWeightVariance, scReportMonthly, scReviewQueue,
  scDashboardAnalytics,
} from "../../api/client";
import { JALALI_MONTHS, JALALI_YEARS, currentJalali } from "../../utils/dateUtils";
import { byStr, byNum } from "../../utils/sorters";
import { CATEGORY_LABEL, eventLabel, fmtKg, statFmtKg } from "./scConstants";
import GxTable from "../../components/GxTable";
import GxTabs from "../../components/GxTabs";
import CostPerKgTab from "./CostPerKgTab";
import WarehouseInventoryTable from "./WarehouseInventoryTable";
import { faNum } from "../../utils/faNumberFormat";

const { Option } = Select;
const faPct = (v) => (v == null ? "—" : `${faNum(Number(v), { maximumFractionDigits: 2 })}٪`);

export default function SupplyChainReports() {
  const cj = currentJalali();
  const [balance, setBalance] = useState([]);
  const [variance, setVariance] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [queue, setQueue] = useState(null);
  const [year, setYear] = useState(cj.year);
  const [month, setMonth] = useState(cj.month);
  const [bq, setBq] = useState("");      // جست‌وجوی ماندهٔ امانی
  const [waste, setWaste] = useState(null);  // تحلیلِ ضایعاتِ گذارها

  useEffect(() => {
    scReportPartyBalance().then((r) => setBalance(r.data)).catch(() => {});
    scReportWeightVariance().then((r) => setVariance(r.data.rows || [])).catch(() => {});
    scReviewQueue().then((r) => setQueue(r.data)).catch(() => {});
    scDashboardAnalytics({}).then((r) => setWaste(r.data)).catch(() => {});
  }, []);

  const loadMonthly = (y, m) => {
    scReportMonthly(y, m).then((r) => setMonthly(r.data.by_event_type || [])).catch(() => {});
  };
  useEffect(() => { loadMonthly(year, month); }, [year, month]);

  const balanceCols = [
    { title: "طرف", dataIndex: "party_name", sorter: byStr("party_name"), gxFilter: "enum" },
    { title: "کالا", dataIndex: "item_category", sorter: byStr("item_category"),
      gxFilter: "enum",
      gxFilterOptions: Object.entries(CATEGORY_LABEL).map(([v, l]) => ({ text: l, value: v })),
      render: (v) => <Tag>{CATEGORY_LABEL[v] || v}</Tag> },
    { title: "ماندهٔ امانی", dataIndex: "current_balance_kg", align: "center", sorter: byNum("current_balance_kg"),
      defaultSortOrder: "descend", gxFilter: "num",
      render: (v) => <b style={{ color: v > 0 ? "#107C41" : "#888" }}>{fmtKg(v)}</b> },
  ];
  const varianceCols = [
    { title: "کد رویداد", dataIndex: "event_code", sorter: byStr("event_code") },
    { title: "بافنده", dataIndex: "party_name", sorter: byStr("party_name"), gxFilter: "enum" },
    { title: "خروجی پارچه", dataIndex: "output_weight_kg", align: "center", sorter: byNum("output_weight_kg"),
      gxFilter: "num", render: (v) => fmtKg(v) },
    { title: "درصد ضایعات مجاز", dataIndex: "allowed_waste_percent", align: "center", sorter: byNum("allowed_waste_percent"),
      gxFilter: "num", render: (v) => (v != null ? `${v}٪` : "—") },
    { title: "ضایعات مجاز", dataIndex: "allowed_waste_kg", align: "center", sorter: byNum("allowed_waste_kg"),
      gxFilter: "num", render: (v) => (v != null ? fmtKg(v) : "—") },
  ];
  const monthlyCols = [
    { title: "نوع رویداد", dataIndex: "event_type", sorter: byStr("event_type"),
      gxFilter: "enum",
      render: (v) => eventLabel(v) },
    { title: "مجموع وزن", dataIndex: "weight_kg", align: "center", sorter: byNum("weight_kg"),
      gxFilter: "num", render: (v) => fmtKg(v) },
  ];

  const balanceSummary = [
    { dataIndex: "party_name", type: "unique" },
    { dataIndex: "item_category", type: "unique", labelOf: (v) => CATEGORY_LABEL[v] || v },
    { dataIndex: "current_balance_kg", type: "sum", format: (v) => fmtKg(v) },
  ];
  const monthlySummary = [
    { dataIndex: "event_type", type: "unique", labelOf: eventLabel },
    { dataIndex: "weight_kg", type: "sum", format: (v) => fmtKg(v) },
  ];
  const varianceSummary = [
    { dataIndex: "party_name", type: "unique" },
    { dataIndex: "output_weight_kg", type: "sum", format: (v) => fmtKg(v) },
    { dataIndex: "allowed_waste_kg", type: "sum", format: (v) => fmtKg(v) },
  ];

  const q = bq.trim();
  const balanceShown = q
    ? balance.filter((b) => `${b.party_name} ${CATEGORY_LABEL[b.item_category] || b.item_category}`.includes(q))
    : balance;

  const tabs = [
    {
      key: "cost", label: "فی تمام‌شده",
      children: <CostPerKgTab />,
    },
    {
      key: "inventory", label: "موجودی انبار",
      children: (
        <Card title="موجودی انبار قرچک (با فی تمام‌شدهٔ زنجیره)">
          <WarehouseInventoryTable />
        </Card>
      ),
    },
    {
      key: "balance", label: "ماندهٔ امانیِ هر طرف",
      children: (
        <Card extra={
          <Input.Search allowClear placeholder="جست‌وجوی طرف/کالا…" style={{ width: 240 }}
            value={bq} onChange={(e) => setBq(e.target.value)} />
        }>
          <GxTable dataSource={balanceShown} columns={balanceCols}
            layoutId="sc-report-balance"
            rowKey={(r) => `${r.party_id}-${r.item_type_id}`} size="small" pagination={false}
            scroll={{ x: "max-content" }} gxSummary={balanceSummary} />
        </Card>
      ),
    },
    {
      key: "monthly", label: "ماهانه",
      children: (
        <Card extra={
          <Space>
            <Select value={year} onChange={setYear} style={{ width: 100 }}>
              {JALALI_YEARS.map((y) => <Option key={y} value={y}>{y}</Option>)}
            </Select>
            <Select value={month} onChange={setMonth} style={{ width: 110 }}>
              {JALALI_MONTHS.map((m, i) => <Option key={i + 1} value={i + 1}>{m}</Option>)}
            </Select>
          </Space>
        }>
          <GxTable dataSource={monthly} columns={monthlyCols} rowKey="event_type" size="small"
            layoutId="sc-report-monthly"
            pagination={false} scroll={{ x: "max-content" }} gxSummary={monthlySummary} />
        </Card>
      ),
    },
    {
      key: "waste", label: "داشبوردِ ضایعات",
      children: (
        <div>
          <Card size="small" style={{ marginBottom: 12, background: "var(--bg-subtle, #fafafa)" }}>
            <span style={{ color: "#8c8c8c", fontSize: 13 }}>
              ضایعاتِ هر گذار = وزنِ ورودی − وزنِ خروجی. طبیعی است و فقط برای گزارش؛ در حساب‌وکتاب وارد نمی‌شود.
            </span>
          </Card>
          <Row gutter={[12, 12]}>
            {(waste?.transitions || []).map((t) => (
              <Col xs={24} md={12} key={t.key}>
                <Card size="small" title={t.title}
                  extra={!t.has_data ? <Tag>بدون داده</Tag>
                    : <Tag color={t.within_tolerance ? "green" : "red"}>{t.within_tolerance ? "سالم" : "هشدار"}</Tag>}>
                  <Row gutter={8}>
                    <Col xs={8}><Statistic title={t.in_label} value={t.in_kg ?? 0} formatter={statFmtKg} valueStyle={{ fontSize: 17 }} /></Col>
                    <Col xs={8}><Statistic title={t.out_label} value={t.out_kg ?? 0} formatter={statFmtKg} valueStyle={{ fontSize: 17 }} /></Col>
                    <Col xs={8}><Statistic title="ضایعات" value={t.loss_kg ?? 0} formatter={statFmtKg}
                      valueStyle={{ fontSize: 17, color: t.within_tolerance ? "#389e0d" : "#cf1322" }}
                      suffix={t.loss_pct != null ? ` (${faPct(t.loss_pct)})` : ""} /></Col>
                  </Row>
                  {t.tolerance_pct != null && (
                    <div style={{ marginTop: 6, fontSize: 12, color: "#8c8c8c" }}>تلورانسِ مجاز: {faPct(t.tolerance_pct)}</div>
                  )}
                </Card>
              </Col>
            ))}
          </Row>
          {waste?.overall && (
            <Card size="small" style={{ marginTop: 12, borderColor: "#722ed1" }}
              title="کلِ زنجیره (نخِ ورودی ← رنگ‌شدهٔ واردِ انبار)">
              <Row gutter={8}>
                <Col xs={8}><Statistic title={waste.overall.in_label} value={waste.overall.in_kg ?? 0} formatter={statFmtKg} /></Col>
                <Col xs={8}><Statistic title={waste.overall.out_label} value={waste.overall.out_kg ?? 0} formatter={statFmtKg} /></Col>
                <Col xs={8}><Statistic title="ضایعاتِ کل" value={waste.overall.loss_kg ?? 0} formatter={statFmtKg}
                  valueStyle={{ color: waste.overall.within_tolerance ? "#389e0d" : "#cf1322" }}
                  suffix={waste.overall.loss_pct != null ? ` (${faPct(waste.overall.loss_pct)})` : ""} /></Col>
              </Row>
            </Card>
          )}
          {!(waste?.transitions || []).length && (
            <div style={{ textAlign: "center", color: "#bbb", padding: 24 }}>داده‌ای برای ضایعات نیست</div>
          )}
        </div>
      ),
    },
    {
      key: "variance", label: "واریانس ضایعات بافت",
      children: <Card><GxTable dataSource={variance} columns={varianceCols} rowKey="event_code"
        layoutId="sc-report-variance"
        size="small" pagination={{ pageSize: 15 }} scroll={{ x: "max-content" }}
        gxSummary={varianceSummary} /></Card>,
    },
    {
      key: "queue", label: "صف بررسی",
      children: queue && (
        <div>
          <Row gutter={12} style={{ marginBottom: 12 }}>
            <Col xs={8}><Card size="small"><Statistic title="نیازمند بررسی/مغایر" value={queue.counts.needs_review}
              valueStyle={{ color: queue.counts.needs_review ? "#d46b08" : undefined }} /></Card></Col>
            <Col xs={8}><Card size="small"><Statistic title="تأییدشده بدون بچ" value={queue.counts.without_batch} /></Card></Col>
            <Col xs={8}><Card size="small"><Statistic title="تأییدشده بدون سند" value={queue.counts.without_document} /></Card></Col>
          </Row>
          <Card title="رویدادهای نیازمند بررسی / مغایر">
            <GxTable size="small" pagination={false} rowKey="id"
              layoutId="sc-report-queue"
              dataSource={queue.needs_review_or_disputed}
              gxSummary={[
                { dataIndex: "event_type", type: "unique", labelOf: eventLabel },
                { dataIndex: "weight_kg", type: "sum", format: (v) => fmtKg(v) },
                { dataIndex: "status", type: "unique" },
              ]}
              columns={[
                { title: "کد", dataIndex: "event_code", sorter: byStr("event_code") },
                { title: "نوع", dataIndex: "event_type", sorter: byStr("event_type"),
                  gxFilter: "enum", render: (v) => eventLabel(v) },
                { title: "وزن", dataIndex: "weight_kg", sorter: byNum("weight_kg"),
                  gxFilter: "num", render: (v) => fmtKg(v) },
                { title: "وضعیت", dataIndex: "status", sorter: byStr("status"), gxFilter: "enum" },
              ]} />
          </Card>
        </div>
      ),
    },
  ];

  return (
    <div>
      <h2 style={{ marginTop: 0 }}>گزارش‌های زنجیره تأمین</h2>
      <GxTabs urlKey="tab" items={tabs} />
    </div>
  );
}
